import * as React from "react"
import type { User } from "firebase/auth"
import { subscribeToAuthState, logout } from "../lib/authClient"

const isBrowser = typeof window !== "undefined"

interface AuthContextType {
  user: User | null
  isAdmin: boolean
  loading: boolean
  logout: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

const defaultValue: AuthContextType = {
  user: null,
  isAdmin: false,
  loading: true,
  logout: async () => {},
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(!isBrowser)

  React.useEffect(() => {
    if (!isBrowser) return

    const unsubscribe = subscribeToAuthState((firebaseUser) => {
      setUser(firebaseUser)
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const handleLogout = React.useCallback(async () => {
    if (!isBrowser) return
    await logout()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: user !== null,
        loading,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
